import { ZodSchema } from "zod";
import { z } from "zod";

import { PyFormApi } from "./types";

// types
import { InputWrapperProps } from "../../molecules";

/**
 * Creates a PyFormApi instance from a form library instance
 * @param form - The form library's form instance
 * @returns A PyFormApi instance
 */
export const createPyFormApi = <TSchema extends ZodSchema>(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: any
): Pick<
	PyFormApi<TSchema>,
	"state" | "reset" | "Field" | "Subscribe" | "getFieldValue" | "getFieldMeta" | "setFieldMeta"
> => {
	return {
		state: {
			values: form?.state?.values as z.infer<TSchema>,
			isDirty: form?.state?.isDirty,
			isSubmitting: form?.state?.isSubmitting,
			canSubmit: form?.state?.canSubmit,
		},
		reset: form.reset,
		Field: form.Field as PyFormApi<TSchema>["Field"],
		Subscribe: form.Subscribe as PyFormApi<TSchema>["Subscribe"],
		getFieldValue: form.getFieldValue,
		getFieldMeta: form.getFieldMeta,
		setFieldMeta: form.setFieldMeta,
	};
};

/**
 * this function separates out the input wrapper props from the rest of the props
 * @param props - The props of the input wrapper
 * @returns The input wrapper props and the rest of the props
 */
export const separateInputWrapperPropsAndInputProps = (props: InputWrapperProps, formId: string) => {
	const { id, label, labelPosition, info, error, disabled, required, disableLabelClick, ...rest } = props;
	const inputWrapperProps: InputWrapperProps = {
		"data-testid": `${formId}-${id}`,
		id,
		label,
		labelPosition,
		info,
		error,
		disabled,
		// must also be destructured above, or it lands on the input as a native required attribute
		required,
		disableLabelClick,
	};
	const inputProps = rest;
	return { inputWrapperProps, inputProps };
};

/**
 * This function gets the error messages from the zod errors
 * @param errors - The zod errors
 * @returns The error messages
 */
export const getErrorMessagesFromZodErrors = (errors: { message: string }[] = []) => {
	return errors[0]?.message || "";
};
